import {
  type CodigoDePapel,
  PAPEIS,
  PAPEIS_INTERNOS,
  STATUS_DE_USUARIO,
  atualizarUsuarioInternoSchema,
  criarUsuarioInternoSchema,
  emailSchema,
  nomeSchema,
  type StatusDoUsuario,
  telefoneSchema,
} from '@locaveri/shared'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import {
  aplicarErrosDaApi,
  aplicarErrosDoZod,
  validarCom,
  validarComSeInformado,
} from '@/lib/formulario'
import { apenasDigitos, mascararTelefoneProgressivo } from '@/lib/mascaras'

import { useAtualizarUsuario, useCriarUsuario, type Usuario } from './api'

/**
 * Cadastro e edição de usuário interno.
 *
 * ---------------------------------------------------------------------------
 * NINGUÉM DIGITA A SENHA DE OUTRA PESSOA
 * ---------------------------------------------------------------------------
 * O formulário de criação não tem campo de senha. O usuário nasce `PENDENTE` e
 * recebe um convite — o mesmo mecanismo do reset — para definir a própria
 * senha. Assim a credencial de terceiro nunca passa pela tela (nem pela memória)
 * de quem cadastra, e o e-mail fica verificado no mesmo movimento.
 *
 * O papel é MÚLTIPLO porque acumular papel é comum de verdade: em operação
 * enxuta a mesma pessoa é FINANCEIRO e ATENDIMENTO. `CLIENTE` não aparece na
 * lista — ele é do app, e o próprio schema o recusa.
 */

const CAMPOS = ['nome', 'email', 'telefone', 'papeis', 'status'] as const

const ROTULO_DE_STATUS: Readonly<Record<StatusDoUsuario, string>> = {
  ATIVO: 'Ativo',
  PENDENTE: 'Pendente',
  BLOQUEADO: 'Bloqueado',
}

interface FormularioDeUsuario {
  nome: string
  email: string
  telefone: string
  papeis: string[]
  enviarConvite: boolean
  status: StatusDoUsuario
}

export interface DialogoDeUsuarioProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cria; preenchido edita. */
  readonly usuario?: Usuario | undefined
}

export function DialogoDeUsuario({
  aberto,
  aoFechar,
  usuario,
}: DialogoDeUsuarioProps): ReactElement {
  const editando = usuario !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criar = useCriarUsuario()
  const atualizar = useAtualizarUsuario()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDeUsuario>({
    defaultValues: {
      nome: '',
      email: '',
      telefone: '',
      papeis: [],
      enviarConvite: true,
      status: 'PENDENTE',
    },
  })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir "editar"
  // depois de "novo" mostraria os valores do formulário anterior.
  useEffect(() => {
    if (!aberto) {
      return
    }

    setErroGeral(null)
    reset({
      nome: usuario?.nome ?? '',
      email: usuario?.email ?? '',
      telefone: mascararTelefoneProgressivo(usuario?.telefone ?? ''),
      papeis: usuario === undefined ? [] : [...usuario.papeis],
      enviarConvite: true,
      status: usuario?.status ?? 'PENDENTE',
    })
  }, [aberto, reset, usuario])

  const telefone = watch('telefone')

  function papeisSelecionados(valores: readonly string[]): CodigoDePapel[] {
    // Filtrar o catálogo (em vez de converter a lista do formulário) devolve
    // `CodigoDePapel[]` sem nenhuma conversão de tipo e ainda mantém a ordem
    // canônica dos papéis.
    return PAPEIS_INTERNOS.filter((codigo) => valores.includes(codigo))
  }

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const telefoneLimpo = apenasDigitos(valores.telefone)
    const papeis = papeisSelecionados(valores.papeis)

    if (usuario !== undefined) {
      const entrada = {
        nome: valores.nome,
        papeis,
        status: valores.status,
        ...(telefoneLimpo.length > 0 ? { telefone: telefoneLimpo } : {}),
      }

      const validado = atualizarUsuarioInternoSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      atualizar.mutate(
        { id: usuario.id, dados: validado.data },
        {
          onSuccess: () => {
            toast.success('Usuário atualizado')
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    const entrada = {
      nome: valores.nome,
      email: valores.email,
      papeis,
      enviarConvite: valores.enviarConvite,
      ...(telefoneLimpo.length > 0 ? { telefone: telefoneLimpo } : {}),
    }

    const validado = criarUsuarioInternoSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    criar.mutate(validado.data, {
      onSuccess: (criado) => {
        toast.success('Usuário criado', {
          description: valores.enviarConvite
            ? `Enviamos o convite para ${criado.email}.`
            : 'Nenhum convite foi enviado — o acesso fica pendente até você reenviar.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  const ocupado = criar.isPending || atualizar.isPending

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar usuário' : 'Novo usuário interno'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'Altere os dados e os papéis. O e-mail não muda — ele é a identidade do acesso.'
              : 'A pessoa recebe um convite por e-mail para definir a própria senha.'}
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <Campo rotulo="Nome" obrigatorio erro={errors.nome?.message}>
            {(campo) => (
              <Input
                {...campo}
                {...register('nome', {
                  required: 'informe o nome',
                  validate: validarCom(nomeSchema),
                })}
                autoComplete="name"
                disabled={ocupado}
              />
            )}
          </Campo>

          <Campo
            rotulo="E-mail"
            obrigatorio
            erro={errors.email?.message}
            dica={editando ? 'O e-mail de um usuário existente não pode ser alterado.' : undefined}
          >
            {(campo) => (
              <Input
                {...campo}
                {...register('email', {
                  required: 'informe o e-mail',
                  validate: validarCom(emailSchema),
                })}
                type="email"
                autoComplete="email"
                disabled={ocupado || editando}
                readOnly={editando}
              />
            )}
          </Campo>

          <Campo rotulo="Telefone" erro={errors.telefone?.message} dica="Opcional.">
            {(campo) => (
              <Input
                {...campo}
                {...register('telefone', { validate: validarComSeInformado(telefoneSchema) })}
                value={telefone}
                onChange={(evento) => {
                  setValue('telefone', mascararTelefoneProgressivo(evento.target.value), {
                    shouldValidate: isSubmitted,
                  })
                }}
                inputMode="tel"
                autoComplete="tel"
                placeholder="(11) 98888-7777"
                disabled={ocupado}
              />
            )}
          </Campo>

          <fieldset className="space-y-2" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Papéis</legend>
            <p className="text-xs text-fg-muted">
              As permissões de cada papel são configuráveis pelo gestor — o que está aqui é o
              vínculo da pessoa com o papel.
            </p>

            <div className="grid gap-2 sm:grid-cols-2">
              {PAPEIS_INTERNOS.map((codigo) => (
                <label
                  key={codigo}
                  className="flex cursor-pointer items-start gap-2 rounded-lg border
                    border-border bg-surface p-3 text-sm"
                >
                  <Checkbox
                    value={codigo}
                    {...register('papeis', {
                      validate: (valor) => valor.length > 0 || 'selecione pelo menos um papel',
                    })}
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="block font-medium text-foreground">{PAPEIS[codigo].nome}</span>
                    <span className="block text-xs text-fg-secondary">
                      {PAPEIS[codigo].descricao}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            {errors.papeis?.message === undefined ? null : (
              <p className="text-xs text-danger-fg">{errors.papeis.message}</p>
            )}
          </fieldset>

          {editando ? (
            <Campo rotulo="Situação" erro={errors.status?.message}>
              {(campo) => (
                <Select {...campo} {...register('status')} disabled={ocupado}>
                  {STATUS_DE_USUARIO.map((status) => (
                    <option key={status} value={status}>
                      {ROTULO_DE_STATUS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
          ) : (
            <label className="flex cursor-pointer items-start gap-2 text-sm text-fg-secondary">
              <Checkbox {...register('enviarConvite')} className="mt-0.5" disabled={ocupado} />
              <span>Enviar o convite por e-mail agora</span>
            </label>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Criar usuário'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
